import React, { ReactNode } from 'react';
import { selectAuthenticated } from '../../services/redux/authSlice';
import { useAppSelector } from '../redux/hooks';
import OrbotnikIcon from '../../components/common/Icons/Icons';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Render a component conditionally based on the authentication status.
 *
 * @param component - The component to render when the authentication status meets the specified conditions.
 * @param mode - The mode determines how the component is rendered based on the authentication status.
 *   - "show": Render the component if the user is authenticated, otherwise render null.
 *   - "hide": Render null if the user is authenticated, otherwise render the component.
 *   - "enable": Render the component (no effect on the disabled state).
 *   - "disable": Render the component (no effect on the disabled state).
 * @param props - Additional props to pass to the component.
 * @returns The rendered component based on the authentication status and mode.
 */
export function ConditionalRenderWithAuth({ component: Component, mode, ...props }: {
                                                                                      component: ReactNode; 
                                                                                      mode: "show" | "hide" | "enable" | "disable";
                                                                                    } & any) {
  const isAuthenticated = useAppSelector(selectAuthenticated); 

  switch (mode) {
    case 'show':
      return isAuthenticated ? Component : null;
    case 'hide':
      return isAuthenticated ? null : Component;
    case 'enable':
    case 'disable':
      // These modes don't really make sense anymore with a ReactNode,
      // because you can't dynamically add 'disabled' prop to an already instantiated ReactNode.
      return Component; 
    default:
      return null; 
  }
}



export interface SettingType {
  key: string;
  iconname?: string;
  tooltip?: string;
  caption?: string;
  route?: string;
  authmode?: 'show' | 'hide' | 'enable' | 'disable';
  color?: "inherit" | "disabled" | "action" | "error" | "primary" | "secondary" | "info" | "success" | "warning" | "contrast";
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  margin?: string | number;
  sxstyles?: Record<string, any>;
  subTabs?:SettingType[]
}
interface IconButtonWithAuthProps {
  setting: SettingType;

}
type IconButtonColor = "inherit" | "success" | "error" | "warning" | "primary" | "secondary" | "info" | "default";

function getIconButtonColor(color: SettingType['color']): IconButtonColor {
  const validColors: IconButtonColor[] = ["inherit", "success", "error", "warning", "primary", "secondary", "info", "default"];
  
  return color && color in validColors? color as IconButtonColor : "default";
}
export const IconButtonWithAuth: React.FC<IconButtonWithAuthProps> = ({ setting }) => {
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (setting.onClick) {
      setting.onClick(event);
    }
    if (setting.route) {
      navigate(setting.route);
    }
  };

  return (
    <ConditionalRenderWithAuth key={setting.key} fallbackComponent={<></>} mode={setting.authmode} component={(
      <Tooltip title={setting.tooltip} key={setting.iconname}>
        <IconButton
          size="large"
          aria-label={setting.caption}
          aria-controls="menu-appbar"
          aria-haspopup="true"
          color={getIconButtonColor(setting.sxstyles?.color)}
          onClick={handleClick}
          sx={{
            ...setting.sxstyles , 
          }}  
        >
          <OrbotnikIcon iconName={setting.iconname!} color={setting.color} />
        </IconButton>
      </Tooltip>
    )} />
  );
}


interface ButtonWithAuthProps {
  setting: SettingType;
}

export const ButtonWithAuth: React.FC<ButtonWithAuthProps> = ({ setting }) => {
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (setting.onClick) {
      setting.onClick(event);
    }
    if (setting.route) {
      navigate(setting.route);
    }
  };
  return (
    <ConditionalRenderWithAuth key={setting.key} fallbackComponent={<></>} mode={setting.authmode} component={(
      <Tooltip title={setting.tooltip} key={setting.iconname}>
        <Button
          sx={{
            my: 2, 
            display: 'block',
            color: "default",
            ...setting.sxstyles , 
          }}
          onClick={handleClick}
        >
          {setting.caption}
        </Button>          
      </Tooltip>
    )} />
  );
}
